/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Link } from 'react-router-dom';
import isAfter from 'date-fns/isAfter';
import differenceInDays from 'date-fns/differenceInDays';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  CustomIcons,
  getPPPDString,
  getTotalBudgetString,
} from '@marageti/z4-lib';
import { ChecklistItem, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { titleCase } from '../../../../utils/helpers';
import { formatCountryCodesToString } from '../../utils';
import { TravelerChips, RefineSoldChip } from '../../chips';
import { ChatIndicator } from '../../trip-indicators';

const {
  OutlinedUserGroupIcon,
  OutlinedLocationMarkerIcon,
  OutlinedCurrencyDollarIcon,
  OutlinedUserIcon,
  OutlinedArchiveIcon,
} = OutlinedIcons;

const {
  DepartureIcon,
} = CustomIcons;

const IconLabel = ({ icon, label }: { icon: any, label: any }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <SvgIcon color="disabled" component={icon} />
    <Typography variant="caption">
      {label}
    </Typography>
  </Stack>
);

type SoldCardViewProps = {
  countryCodes: CountryCodeMap | undefined;
  tripMatch: TripMatch;
  showReturnDate: boolean;
  onDismiss: (tripMatchId: string) => void;
};

const SoldCardView = ({
  countryCodes,
  tripMatch,
  showReturnDate,
  onDismiss,
}: SoldCardViewProps) => {
  const {
    assignedTo,
    travelers,
    countries,
    checklist,
    departureDate,
    totalNights,
    numberOfAdults,
    numberOfChildren,
    partnerName,
    createdAt,
    referralType,
    returnDate,
    repeatTraveler,
    tripMessageState,
    status,
    totalPppd,
    totalTripPrice,
  } = tripMatch;
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const fullName = titleCase(travelers[0].fullName);
  const countryNames = countryCodes ? formatCountryCodesToString(countries, countryCodes) : '';
  const departureDateString = getFormattedDateShortMonthDDYYYY(departureDate) || '';
  const returnDateString = getFormattedDateShortMonthDDYYYY(returnDate) || '';
  const totalNightsString = `${totalNights} Nights`;
  const numberOfTravelers = numberOfAdults + numberOfChildren;
  const totalTravelersString = numberOfTravelers === 1
    ? '1 Traveler'
    : `${numberOfTravelers} Travelers`;
  const matchedDate = getFormattedDateShortMonthDDYYYY(createdAt) || '';
  const showDismissButton = isAfter(new Date(), new Date(returnDate))
    && differenceInDays(new Date(), new Date(returnDate)) <= 91
    && checklist.some((item) => [ChecklistItem.NeedsZicassoReview, ChecklistItem.NeedsZicassoReviewRequest].includes(item));

  return (
    <Card
      component={Link}
      sx={{
        '&:hover': {
          bgcolor: 'grey.A100',
        },
        textDecoration: 'none',
      }}
      to={`/trip-matches/${tripMatch?.id}`}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1}>
              <Typography variant="bodyBold">
                {fullName}
              </Typography>
              <TravelerChips
                travelers={travelers}
                referralType={referralType}
                repeatTraveler={repeatTraveler}
              />
            </Stack>

            <Stack direction="column" spacing={1}>
              <IconLabel icon={OutlinedUserGroupIcon} label={totalTravelersString} />
              <IconLabel icon={OutlinedLocationMarkerIcon} label={countryNames} />
              <IconLabel
                icon={DepartureIcon}
                label={(
                  showReturnDate ? `Returned: ${returnDateString}` : (
                    <>
                      {departureDateString}
                      {' '}
                      ·
                      {' '}
                      {totalNightsString}
                    </>
                  )
                )}
              />
              <IconLabel
                icon={OutlinedCurrencyDollarIcon}
                label={(
                  <>
                    {getTotalBudgetString(totalTripPrice.amount, 2, 2, totalTripPrice.currencyCode)}
                    {' '}
                    Total
                    {' '}
                    ·
                    {' '}
                    {getPPPDString(totalPppd, totalTripPrice.currencyCode)}
                  </>
                )}
              />
              <IconLabel icon={OutlinedUserIcon} label={partnerName} />
            </Stack>

            <Grid
              container
              alignItems="center"
            >
              <Grid item xs={6} display="flex">
                <RefineSoldChip
                  checklist={checklist}
                  departureDate={departureDate}
                  returnDate={returnDate}
                  status={status}
                />
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end" gap={1.5}>
                <ChatIndicator
                  assignedTo={assignedTo}
                  createdAt={createdAt}
                  isAgentContext
                  tripMatchStatus={status}
                  tripMessageState={tripMessageState}
                />
                <Tooltip
                  title={showDismissButton ? 'Clear from List' : ''}
                >
                  <span
                    style={{
                      cursor: showDismissButton ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <IconButton
                      disabled={!showDismissButton}
                      onClick={(e) => {
                        e.preventDefault();
                        onDismiss(tripMatch.id);
                      }}
                      sx={{ padding: 0 }}
                    >
                      <OutlinedArchiveIcon color={showDismissButton ? 'primary' : 'disabled'} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="center" columnSpacing={1}>
            <Grid item xs={3}>
              <Stack direction="row" spacing={1}>
                <Typography variant="bodyBold">
                  {fullName}
                </Typography>
                <TravelerChips
                  travelers={travelers}
                  referralType={referralType}
                  repeatTraveler={repeatTraveler}
                />
              </Stack>
              <Typography variant="caption">
                {countryNames}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {showReturnDate ? `Returned: ${returnDateString}` : departureDateString}
              </Typography>
              <Typography variant="caption">
                {totalNightsString}
                {' '}
                ·
                {' '}
                {totalTravelersString}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {getPPPDString(totalPppd, totalTripPrice.currencyCode)}
              </Typography>
              <Typography variant="caption">
                {`${getTotalBudgetString(totalTripPrice.amount, 2, 2, totalTripPrice.currencyCode)} Total Trip Price`}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {partnerName}
              </Typography>
              <Typography variant="caption">
                Matched:
                {' '}
                {matchedDate}
              </Typography>
            </Grid>

            <Grid item xs={1.5}>
              <Stack
                direction="row"
                justifyContent="center"
                spacing={1}
                alignItems="center"
              >
                <ChatIndicator
                  assignedTo={assignedTo}
                  createdAt={createdAt}
                  isAgentContext
                  tripMatchStatus={status}
                  tripMessageState={tripMessageState}
                  sx={{ position: 'relative', top: '2px' }}
                />
                <Tooltip
                  title={showDismissButton ? 'Clear from List' : ''}
                >
                  <span
                    style={{
                      cursor: showDismissButton ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <IconButton
                      disabled={!showDismissButton}
                      onClick={(e) => {
                        e.preventDefault();
                        onDismiss(tripMatch.id);
                      }}
                    >
                      <OutlinedArchiveIcon color={showDismissButton ? 'primary' : 'disabled'} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Grid>
            <Grid item xs={1.5} display="flex" justifyContent="flex-end">
              <RefineSoldChip
                checklist={checklist}
                departureDate={departureDate}
                returnDate={returnDate}
                status={status}
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default SoldCardView;
